/**
 * 全景查看器纹理缓存
 * 管理Three.js纹理的加载、缓存和释放，避免重复加载和内存泄漏
 */
import * as THREE from 'three';

interface CacheEntry {
  texture: THREE.Texture;
  lastUsed: number;
  isLoading: boolean;
}

class PanoramaTextureCache {
  private cache: Map<string, CacheEntry> = new Map();
  private maxCacheSize: number = 8;
  private cleanupInterval: ReturnType<typeof setInterval> | null = null;

  constructor() {
    // 定期清理过期纹理
    this.cleanupInterval = setInterval(() => this.cleanup(), 60000);
  }

  async loadTexture(
    url: string,
    renderer: THREE.WebGLRenderer,
    options?: { anisotropy?: number; timeout?: number; retries?: number; retryDelay?: number }
  ): Promise<THREE.Texture> {
    const existing = this.cache.get(url);
    if (existing && !existing.isLoading) {
      existing.lastUsed = Date.now();
      return existing.texture;
    }

    const entry: CacheEntry = {
      texture: new THREE.Texture(),
      lastUsed: Date.now(),
      isLoading: true,
    };
    this.cache.set(url, entry);

    return new Promise((resolve, reject) => {
      const loader = new THREE.TextureLoader();
      loader.load(
        url,
        (texture) => {
          if (options?.anisotropy) {
            texture.anisotropy = Math.min(
              options.anisotropy,
              renderer.capabilities.getMaxAnisotropy()
            );
          }
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.needsUpdate = true;

          entry.texture = texture;
          entry.isLoading = false;
          entry.lastUsed = Date.now();

          this.enforceMaxSize();
          resolve(texture);
        },
        undefined,
        (error) => {
          this.cache.delete(url);
          reject(error);
        }
      );
    });
  }

  releaseTexture(url: string): void {
    const entry = this.cache.get(url);
    if (entry && !entry.isLoading) {
      entry.texture.dispose();
      this.cache.delete(url);
    }
  }

  getCacheSize(): number {
    return this.cache.size;
  }

  private enforceMaxSize(): void {
    if (this.cache.size <= this.maxCacheSize) return;

    const entries = Array.from(this.cache.entries())
      .filter(([_, e]) => !e.isLoading)
      .sort((a, b) => a[1].lastUsed - b[1].lastUsed);

    const toRemove = entries.slice(0, this.cache.size - this.maxCacheSize);
    for (const [url, entry] of toRemove) {
      entry.texture.dispose();
      this.cache.delete(url);
    }
  }

  private cleanup(): void {
    const now = Date.now();
    const maxAge = 5 * 60 * 1000; // 5 minutes

    for (const [url, entry] of this.cache) {
      if (now - entry.lastUsed > maxAge && !entry.isLoading) {
        entry.texture.dispose();
        this.cache.delete(url);
      }
    }
  }

  dispose(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    for (const [_, entry] of this.cache) {
      if (!entry.isLoading) {
        entry.texture.dispose();
      }
    }
    this.cache.clear();
  }
}

export const panoramaTextureCache = new PanoramaTextureCache();
